/**
 * Scope → compact human label + visibility blurb (USAGE: doc/arch/ASSETS.md
 * "scope-pinned" workspaces + the secrets registry).
 *
 * Scope is an ENFORCED confinement boundary (TENANT ⊃ NETWORK ⊃ POOL): a
 * POOL(room)-scoped row is invisible/unattachable outside that room; a NETWORK
 * row only in that network. These helpers SURFACE that boundary in the UI so the
 * user sees where a workspace/connection is confined — they don't change it.
 */

/** Short chip label: "Room only" | "Network" | "Tenant". */
export function scopeBadgeLabel(scope) {
  const k = String(scope?.kind || 'TENANT').toUpperCase();
  if (k === 'POOL') return 'Room only';
  if (k === 'NETWORK') return 'Network';
  return 'Tenant';
}

/** Tailwind chip classes per scope (room = tightest, visually warmest). */
export function scopeBadgeClass(scope) {
  const k = String(scope?.kind || 'TENANT').toUpperCase();
  if (k === 'POOL') return 'bg-amber-50 text-amber-700 ring-amber-200';
  if (k === 'NETWORK') return 'bg-indigo-50 text-indigo-700 ring-indigo-200';
  return 'bg-gray-100 text-gray-600 ring-gray-200';
}

/** Tooltip explaining the confinement (where it's visible / not). */
export function scopeBadgeTitle(scope) {
  const k = String(scope?.kind || 'TENANT').toUpperCase();
  if (k === 'POOL') {
    const pid = String(scope?.poolId ?? scope?.pool_id ?? '').trim();
    return `Confined to this room${pid ? ` (${pid})` : ''} — not visible in other rooms or networks.`;
  }
  if (k === 'NETWORK') {
    const nid = String(scope?.networkId ?? scope?.network_id ?? '').trim();
    return `Visible to every room in this network${nid ? ` (${nid})` : ''} — not other networks.`;
  }
  return 'Visible across the whole workspace (tenant-wide).';
}
